import { useEffect, useMemo, useState } from 'react';

import { MultiShiftCombobox } from '@apps/web/components/outreaches/multi_shift_combobox';
import { OutreachConflictAlert } from '@apps/web/components/outreaches/outreach_conflict_alert';
import { Button } from '@apps/web/components/ui/button';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    type OutreachConflictError,
    parseOutreachConflictError,
} from '@apps/web/lib/outreach_utils';
import { isCancelledShift } from '@apps/web/lib/shift_cancellation';
import {
    useOrganization,
    useShifts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type JoinedOutreachRead,
    type Shift,
} from '@libraries/typescript/api_client';

// Kept in sync with the backend ceilings in
// libraries/python/outreach_operations/constants.py (MULTI_SHIFT_OUTREACH_MAX and
// SAME_CLIENT_MULTI_SHIFT_OUTREACH_MAX).
export const MULTI_SHIFT_OUTREACH_MAX = 15;
// When every selected shift is for the same client (e.g. a full week of
// one client's visits), a larger set is allowed.
export const SAME_CLIENT_MULTI_SHIFT_OUTREACH_MAX = 21;

// Effective multi-shift cap for a selection given its shifts' client ids.
// The same-client cap applies only when the selection has exactly one
// distinct, known client; shifts with no client count as distinct.
export function multiShiftOutreachMax(
    clientIds: Array<string | null | undefined>,
): number {
    const distinctClientIds = new Set(clientIds.map((id) => id ?? null));
    return distinctClientIds.size === 1 && !distinctClientIds.has(null)
        ? SAME_CLIENT_MULTI_SHIFT_OUTREACH_MAX
        : MULTI_SHIFT_OUTREACH_MAX;
}
const SHIFT_WINDOW_DAYS = 14;
const SHIFT_FETCH_LIMIT = 400;

// Treat MISSING shifts like CANCELLED for add-to-outreach purposes: the
// EHR no longer returns the shift so there's nothing useful to target
// with a NEW selection. We still keep MISSING shifts that were on the
// outreach already (initialActiveShiftIds) so coordinators can see
// what they have and explicitly remove them; the banner below flags
// the state.
const isMissingShift = (shift: Shift): boolean => shift.status === 'missing';

// A running v3-born outreach is Phoebe's to change: adding shifts behind its
// back would strand the runner's plan. Cancelling a shift stays open.
const RUNNING_OUTREACH_STATUSES: ReadonlySet<string> = new Set([
    'in_progress',
    'paused',
]);

export function isOutreachShiftAddBlocked(
    outreach: JoinedOutreachRead,
): boolean {
    return (
        outreach.agent_generation === 'v3' &&
        RUNNING_OUTREACH_STATUSES.has(outreach.status)
    );
}

type EditShiftsSectionProps = {
    readonly outreach: JoinedOutreachRead;
    readonly organizationId: string;
    readonly onSuccess: () => void;
    readonly onAskPhoebeToAddShifts?: () => void;
};

export function EditShiftsSection({
    outreach,
    organizationId,
    onSuccess,
    onAskPhoebeToAddShifts,
}: EditShiftsSectionProps) {
    // Shift IDs the user cancelled on this outreach earlier. The
    // cancel endpoint writes these into metadata without popping them
    // from `outreach.shift_ids` and without touching `shift.status` on
    // the row, so we need the metadata check to keep them out of both
    // the preselection and the combobox pool.
    const cancelledShiftIds = useMemo(() => {
        const metadata = outreach.outreach_metadata as {
            cancelled_shifts?: Array<string>;
        } | null;
        return new Set(metadata?.cancelled_shifts ?? []);
    }, [outreach.outreach_metadata]);

    const filledShiftIds = useMemo(() => {
        const metadata = outreach.outreach_metadata as {
            filled_shifts?: Record<string, unknown>;
        } | null;
        return new Set(Object.keys(metadata?.filled_shifts ?? {}));
    }, [outreach.outreach_metadata]);

    // Shifts on the outreach that are still "active" — not cancelled
    // and not filled. These are the ones the user can deselect.
    const initialActiveShiftIds = useMemo(
        () =>
            outreach.shifts
                .filter(
                    (shift) =>
                        !cancelledShiftIds.has(shift.id) &&
                        !filledShiftIds.has(shift.id) &&
                        !isCancelledShift(shift),
                )
                .map((shift) => shift.id),
        [cancelledShiftIds, filledShiftIds, outreach.shifts],
    );

    const [selectedShiftIds, setSelectedShiftIds] = useState<Array<string>>(
        initialActiveShiftIds,
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conflictError, setConflictError] =
        useState<OutreachConflictError | null>(null);
    const { data: organization } = useOrganization(organizationId, {
        reason: 'edit-shifts-section',
    });

    // Snap local state back to the latest outreach data whenever the
    // outreach itself refetches (e.g. after a successful save).
    useEffect(() => {
        setSelectedShiftIds(initialActiveShiftIds);
    }, [initialActiveShiftIds]);

    const shiftQuery = useMemo(() => {
        const start = new Date();
        const end = new Date(
            start.getTime() + SHIFT_WINDOW_DAYS * 24 * 60 * 60 * 1000,
        );
        return {
            shift_start_date: start.toISOString(),
            shift_end_date: end.toISOString(),
            order: 'asc' as const,
            limit: SHIFT_FETCH_LIMIT,
        };
    }, []);

    const { data: shiftsData, isLoading: isLoadingShifts } = useShifts(
        organizationId,
        { reason: 'edit-shifts-section' },
        shiftQuery,
    );

    // Build the combobox pool: the fetched upcoming shifts plus the
    // active shifts already on the outreach (so deselecting them is
    // possible even if they fall outside the fetched window). Shifts
    // the user cancelled on this outreach are filtered out via
    // `cancelledShiftIds` because `useShifts` can still return them
    // (the cancel flow doesn't touch `shift.status`).
    const comboboxShifts = useMemo(() => {
        const now = new Date().toISOString();
        const pool: Array<Shift> = [];
        const seen = new Set<string>();

        for (const shift of outreach.shifts) {
            if (
                !seen.has(shift.id) &&
                initialActiveShiftIds.includes(shift.id)
            ) {
                pool.push(shift);
                seen.add(shift.id);
            }
        }
        for (const shift of shiftsData?.shifts ?? []) {
            if (seen.has(shift.id)) {
                continue;
            }
            if (shift.shift_end_time <= now) {
                continue;
            }
            if (isCancelledShift(shift) || cancelledShiftIds.has(shift.id)) {
                continue;
            }
            if (isMissingShift(shift)) {
                continue;
            }
            pool.push(shift);
            seen.add(shift.id);
        }
        return pool;
    }, [cancelledShiftIds, initialActiveShiftIds, outreach.shifts, shiftsData]);

    // Filled shifts the section does not surface are still tracked on
    // the outreach and should survive a save. We prepend them to the
    // submit body so the PATCH diff only sees the active portion
    // change.
    //
    // Cancelled shifts are intentionally excluded: the backend strips
    // cancelled ids from Outreach.shift_ids at cancel time (PHO-9329),
    // so including them here would re-add them and reintroduce the
    // overlap-claim bug for new outreach creation. Cancelled shifts
    // survive in outreach_metadata['cancelled_shifts'] for rendering.
    const preservedShiftIds = useMemo(
        () =>
            outreach.shifts
                .filter(
                    (shift) =>
                        filledShiftIds.has(shift.id) &&
                        !cancelledShiftIds.has(shift.id) &&
                        !isCancelledShift(shift),
                )
                .map((shift) => shift.id),
        [cancelledShiftIds, filledShiftIds, outreach.shifts],
    );

    const hasChanges = useMemo(() => {
        if (selectedShiftIds.length !== initialActiveShiftIds.length) {
            return true;
        }
        const initialSet = new Set(initialActiveShiftIds);
        return selectedShiftIds.some((id) => !initialSet.has(id));
    }, [initialActiveShiftIds, selectedShiftIds]);

    const handleConfirm = async () => {
        if (!hasChanges) {
            return;
        }
        if (selectedShiftIds.length === 0) {
            toast.error('At least one shift must remain on the callout', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }

        setConflictError(null);
        setIsSubmitting(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/callouts/{callout_id}',
                pathParams: {
                    organization_id: organizationId,
                    callout_id: outreach.id,
                },
                body: {
                    shift_ids: [...preservedShiftIds, ...selectedShiftIds],
                },
            });
            toast.success('Shifts updated', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            onSuccess();
        } catch (error) {
            const conflict = parseOutreachConflictError(error);
            if (conflict) {
                setConflictError(conflict);
            } else {
                const apiError = error as { body?: { detail?: string } };
                toast.error(
                    apiError?.body?.detail ?? 'Failed to update shifts',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-3">
            {conflictError && (
                <OutreachConflictAlert
                    conflict={conflictError}
                    timezone={organization?.timezone}
                />
            )}
            {isOutreachShiftAddBlocked(outreach) ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <p className="text-muted-foreground text-sm sm:flex-1">
                        Phoebe is running this outreach, so ask her to add
                        shifts to it.
                    </p>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="w-full sm:w-auto"
                        disabled={onAskPhoebeToAddShifts === undefined}
                        onClick={onAskPhoebeToAddShifts}
                    >
                        Ask Phoebe to add shifts
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <p className="text-muted-foreground text-sm sm:flex-1">
                        Edit the shifts that caregivers will be contacted about.
                    </p>
                    <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-start">
                        <div className="min-w-0 flex-1 sm:max-w-xs">
                            <MultiShiftCombobox
                                values={selectedShiftIds}
                                onValuesChange={setSelectedShiftIds}
                                shifts={comboboxShifts}
                                isLoading={isLoadingShifts}
                                autoFocusSearch={false}
                            />
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            className="w-full sm:w-auto"
                            onClick={handleConfirm}
                            disabled={
                                isSubmitting ||
                                !hasChanges ||
                                selectedShiftIds.length === 0
                            }
                        >
                            {isSubmitting ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
